import { FunctionComponent, useState } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DefaultButton,
  ILinkStyles,
  IStackStyles,
  ITextStyles,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  Text
} from "@bps/fluent-ui";

import { TraceAnalysis } from "./insightsParser.utils";

interface TraceReportProps {
  report: TraceAnalysis;
  index: number;
}

export const TraceReport: FunctionComponent<TraceReportProps> = ({
  report,
  index
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">(
    "idle"
  );

  const reportCardStyles: IStackStyles = {
    root: {
      border: "1px solid #ddd",
      padding: 12,
      borderRadius: 4
    }
  };

  const clickableHeaderStyles: ITextStyles = {
    root: {
      cursor: "pointer",
      fontWeight: 600
    }
  };

  const flowCardStyles: IStackStyles = {
    root: {
      border: "1px solid #ddd",
      borderRadius: 4,
      padding: 10,
      background: "#fafafa"
    }
  };

  const flowSectionStyles: IStackStyles = {
    root: {
      border: "1px solid #ddd",
      borderRadius: 4,
      padding: 8,
      background: "#fff",
      minWidth: 220,
      maxWidth: 420
    }
  };

  const flowHeadingStyles: ITextStyles = {
    root: {
      fontWeight: 600
    }
  };

  const flowArrowStyles: ITextStyles = {
    root: {
      fontSize: 20,
      fontWeight: 600,
      paddingTop: 28
    }
  };

  const repoLinkStyles: ILinkStyles = {
    root: {
      wordBreak: "break-word"
    }
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(report.rawJson);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  const handleCopyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  const knownTokenClaims = new Set([
    "graphApiBearerToken",
    "platformApiBearerToken"
  ]);

  const isLikelyJwt = (value: string) => {
    if (!value) {
      return false;
    }

    const parts = value.split(".");
    return (
      parts.length === 3 &&
      value.length > 255 &&
      parts.every(part => part.length > 0)
    );
  };

  const isTokenClaim = (claimName: string, values: string[]) => {
    return (
      knownTokenClaims.has(claimName) ||
      values.some(value => isLikelyJwt(value))
    );
  };

  const buildJwtMsLink = (token: string) =>
    `https://jwt.ms/#access_token=${encodeURIComponent(token)}`;

  const renderClaimValue = (claimName: string, values: string[]) => {
    if (isTokenClaim(claimName, values)) {
      const latestToken = [...values]
        .reverse()
        .find(value => isLikelyJwt(value));

      if (latestToken) {
        return (
          <Link href={buildJwtMsLink(latestToken)} target="_blank">
            View token in jwt.ms
          </Link>
        );
      }
    }

    return (
      <span style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
        {values.join(" => ")}
      </span>
    );
  };

  const getTransformationBuckets = (
    rows: Array<{ item: string; name: string; value: string }>
  ) => {
    const inputs = rows.filter(row => row.item === "InputClaim");
    const parameters = rows.filter(row => row.item === "InputParameter");
    const outputs = rows.filter(row => row.item === "OutputClaim");

    return { inputs, parameters, outputs };
  };

  const claimEntries = [...report.claims].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) {
      return "-";
    }

    return DateTime.fromISO(timestamp, { zone: "utc" })
      .setZone(DateTime.now().timeZone)
      .toFormat(DATE_FORMATS.LONG_DATE_TIME_FORMAT);
  };

  const searchRepoLink = (id: string) => {
    return (
      <Link
        href={`https://dev.azure.com/bpcloud-dev/BpCloud/_search?action=contents&text=${id}&type=code&lp=code-Project&filters=ProjectFilters%7BBpCloud%7DRepositoryFilters%7BBps.Platform.BpID.B2CPolicies%7D&pageSize=25&result=DefaultCollection/BpCloud/Bps.Platform.BpID.B2CPolicies/GBmaster//Policies/v1/1.Base.xml`}
        target="_blank"
        styles={repoLinkStyles}
      >
        {id}
      </Link>
    );
  };

  return (
    <Stack
      tokens={{ childrenGap: 12 }}
      styles={{
        root: {
          border: "1px solid #ddd",
          padding: 12,
          borderRadius: 4
        }
      }}
    >
      <Text
        variant="large"
        onClick={() => setIsOpen(x => !x)}
        styles={clickableHeaderStyles}
      >
        {index + 1}. {report.eventInstance} — {report.policyId} —{" "}
        {formatTimestamp(report.timestamp)} — steps:{" "}
        {report.orchestrationSteps || "-"}
      </Text>

      {isOpen && (
        <Stack tokens={{ childrenGap: 12 }} styles={reportCardStyles}>
          <ul>
            <li>Policy: {report.policyId || "-"}</li>
            <li>Correlation ID: {report.correlationId || "-"}</li>
            <li>Timestamp: {formatTimestamp(report.timestamp)}</li>
            <li>Journey completed: {report.journeyCompleted ? "Yes" : "No"}</li>
            <li>
              <Link
                href="https://dev.azure.com/bpcloud-dev/BpCloud/_git/Bps.Platform.BpID.B2CPolicies?path=%2FUniversal_Journey.md&version=GBmaster&_a=preview"
                target="_blank"
              >
                Orchestration steps
              </Link>
              : {report.orchestrationSteps || "-"}
            </li>
          </ul>

          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <DefaultButton text="Copy JSON" onClick={handleCopyJson} />
          </Stack>

          {copyStatus === "copied" && (
            <MessageBar
              messageBarType={MessageBarType.success}
              isMultiline={false}
            >
              JSON copied to clipboard.
            </MessageBar>
          )}

          {copyStatus === "error" && (
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
            >
              Failed to copy JSON to clipboard.
            </MessageBar>
          )}

          {report.exceptions.length > 0 && (
            <Stack tokens={{ childrenGap: 6 }}>
              <Text variant="mediumPlus">Exceptions</Text>
              <ul>
                {report.exceptions.map((ex, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`exception-${i}`}>{ex}</li>
                ))}
              </ul>
            </Stack>
          )}

          {report.technicalProfiles.length > 0 && (
            <Stack tokens={{ childrenGap: 6 }}>
              <Text variant="mediumPlus">Technical profiles</Text>
              <ol>
                {report.technicalProfiles.map((tp, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${tp.id}-${tp.protocol}-${i}`}>
                    {searchRepoLink(tp.id)} ({tp.protocol})
                  </li>
                ))}
              </ol>
            </Stack>
          )}

          {report.validationTechnicalProfiles.length > 0 && (
            <Stack tokens={{ childrenGap: 6 }}>
              <Text variant="mediumPlus">Validation technical profiles</Text>
              <ol>
                {report.validationTechnicalProfiles.map((tp, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${tp}-${i}`}>{searchRepoLink(tp)}</li>
                ))}
              </ol>
            </Stack>
          )}

          {claimEntries.length > 0 && (
            <Stack tokens={{ childrenGap: 6 }}>
              <Text variant="mediumPlus">Claims</Text>
              <div style={tableWrapperStyle}>
                <table style={compactTableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Claim</th>
                      <th style={thStyle}>Value progression</th>
                      <th style={thStyle}>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimEntries.map(claim => {
                      const hasError = claim.issues.some(
                        x => x.severity === "error"
                      );

                      const hasWarning = claim.issues.some(
                        x => x.severity === "warning"
                      );

                      return (
                        <tr
                          key={claim.name}
                          style={
                            // eslint-disable-next-line no-nested-ternary
                            hasError
                              ? errorRowStyle
                              : hasWarning
                              ? warningRowStyle
                              : undefined
                          }
                        >
                          <td
                            style={{
                              ...tdStyle,
                              ...(hasError ? strongCellStyle : undefined)
                            }}
                          >
                            {claim.name}
                          </td>
                          <td style={tdStyle}>
                            {renderClaimValue(claim.name, claim.values)}
                          </td>
                          <td style={tdStyle}>
                            {claim.issues.length === 0 ? (
                              <span style={mutedTextStyle}>—</span>
                            ) : (
                              <ul style={issueListStyle}>
                                {claim.issues.map((issue, index) => (
                                  <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${claim.name}-${issue.type}-${index}`}
                                    style={
                                      issue.severity === "error"
                                        ? errorTextStyle
                                        : warningTextStyle
                                    }
                                  >
                                    {issue.message}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Stack>
          )}

          {report.claimsTransformations.length > 0 && (
            <Stack tokens={{ childrenGap: 10 }}>
              <Text variant="mediumPlus">Claims transformations</Text>

              {report.claimsTransformations.map((group, i) => {
                const {
                  inputs,
                  parameters,
                  outputs
                } = getTransformationBuckets(group.rows);

                return (
                  <Stack
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${group.id || "claims-transformation"}-${i}`}
                    tokens={{ childrenGap: 8 }}
                    styles={flowCardStyles}
                  >
                    <Text styles={{ root: { fontWeight: 600 } }}>
                      {group.id
                        ? searchRepoLink(group.id)
                        : "Unnamed transformation"}
                    </Text>

                    <Stack
                      horizontal
                      wrap
                      tokens={{ childrenGap: 12 }}
                      verticalAlign="start"
                    >
                      <Stack
                        tokens={{ childrenGap: 6 }}
                        styles={flowSectionStyles}
                      >
                        <Text styles={flowHeadingStyles}>Inputs</Text>
                        {inputs.length > 0 ? (
                          <ul style={plainListStyle}>
                            {inputs.map(row => (
                              <li
                                key={`input-${group.id}-${row.name}-${row.value}`}
                              >
                                <b>{row.name}</b>
                                {row.value ? ` = ${row.value}` : ""}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text>-</Text>
                        )}
                      </Stack>

                      <Text styles={flowArrowStyles}>→</Text>

                      <Stack
                        tokens={{ childrenGap: 6 }}
                        styles={flowSectionStyles}
                      >
                        <Text styles={flowHeadingStyles}>Parameters</Text>
                        {parameters.length > 0 ? (
                          <ul style={plainListStyle}>
                            {parameters.map(row => (
                              <li
                                key={`param-${group.id}-${row.name}-${row.value}`}
                              >
                                <b>{row.name}</b>
                                {row.value ? ` = ${row.value}` : ""}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text>-</Text>
                        )}
                      </Stack>

                      <Text styles={flowArrowStyles}>→</Text>

                      <Stack
                        tokens={{ childrenGap: 6 }}
                        styles={flowSectionStyles}
                      >
                        <Text styles={flowHeadingStyles}>Outputs</Text>
                        {outputs.length > 0 ? (
                          <ul style={plainListStyle}>
                            {outputs.map(row => (
                              <li
                                key={`output-${group.id}-${row.name}-${row.value}`}
                              >
                                <b>{row.name}</b>
                                {row.value ? ` = ${row.value}` : ""}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text>-</Text>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
          {report.tokens.length > 0 && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="mediumPlus">Tokens / auth payloads</Text>

              {report.tokens.map((token, tokenIndex) => (
                <Stack
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${token.label || "token"}-${token.contentType ||
                    "unknown"}-${tokenIndex}`}
                  tokens={{ childrenGap: 6 }}
                  styles={flowCardStyles}
                >
                  <Text styles={flowHeadingStyles}>
                    {tokenIndex + 1}. {token.label || "Token"} (
                    {token.contentType || "unknown"})
                  </Text>

                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                      text="Copy"
                      onClick={() => handleCopyText(token.value)}
                    />
                    {token.jwtMsLink && (
                      <Link href={token.jwtMsLink} target="_blank">
                        jwt.ms
                      </Link>
                    )}
                  </Stack>

                  <div style={payloadBoxStyle}>
                    <pre style={payloadPreStyle}>{token.preview}</pre>
                  </div>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const tableWrapperStyle: React.CSSProperties = {
  maxWidth: "100%",
  overflowX: "auto"
};

const compactTableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "auto",
  minWidth: "480px",
  display: "inline-table"
};

const thStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  textAlign: "left",
  padding: "6px",
  background: "#f5f5f5"
};

const tdStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  textAlign: "left",
  padding: "6px",
  verticalAlign: "top",
  wordBreak: "break-word",
  overflowWrap: "anywhere"
};

const plainListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18
};

const payloadBoxStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  background: "#f8f8f8",
  padding: 8,
  overflowX: "auto",
  maxWidth: "100%"
};

const payloadPreStyle: React.CSSProperties = {
  margin: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  fontSize: 12,
  fontFamily: "monospace"
};

const errorRowStyle: React.CSSProperties = {
  backgroundColor: "#fde7e9"
};

const warningRowStyle: React.CSSProperties = {
  backgroundColor: "#fff4ce"
};

const strongCellStyle: React.CSSProperties = {
  fontWeight: 600
};

const mutedTextStyle: React.CSSProperties = {
  color: "#666"
};

const issueListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: "18px"
};

const errorTextStyle: React.CSSProperties = {
  color: "#a4262c"
};

const warningTextStyle: React.CSSProperties = {
  color: "#8a6d1a"
};
